import { FetchResult } from '@apollo/client';
import { useMemo } from 'react';
import {
  Address,
  PartnerGroupRules,
  ProjectStages,
  useGroupRules,
  usePartnerGroupRules,
  UserGroupRules,
} from 'src/graphql';
import { getCorrectSubscriptionIdFromAddress } from 'src/graphql/entities/address/utils';
import { useStripeBillingPortalUrl } from 'src/graphql/entities/payment-portal-url';
import { GetStripeSubscriptionDetailsResponse } from 'src/graphql/entities/stripe-subscription-details';
import {
  CreateStripeSubscriptionScheduleMutation,
  ScheduleCreationParams,
} from 'src/graphql/entities/stripe-subscription-schedule';
import { Modals, useShowModals } from 'src/store/modals';
import { useCopyToClipboard } from 'src/utils/hooks';

export const useMembershipActions = ({
  address,
  conversationId,
  subscriptionDetails,
  addCppToSchedule,
  addPsmToSchedule,
  onUnpauseSubscription,
  isCppDisabled,
}: {
  address: Address;
  conversationId?: string;
  subscriptionDetails?: GetStripeSubscriptionDetailsResponse;
  addCppToSchedule?: (
    params: ScheduleCreationParams,
  ) => Promise<FetchResult<CreateStripeSubscriptionScheduleMutation>> | undefined;
  addPsmToSchedule?: (
    params: ScheduleCreationParams,
  ) => Promise<FetchResult<CreateStripeSubscriptionScheduleMutation>> | undefined;
  onUnpauseSubscription?: () => void;
  isCppDisabled?: boolean;
}) => {
  const { generateStripeBillingPortalUrl } = useStripeBillingPortalUrl();
  const [
    showFollowUpModal,
    showComplimentaryPeriodModal,
    showPreSoldMembershipModal,
    showSendMembershipEmailModal,
    showCustomSignupLinkModal,
    showPauseSubscriptionModal,
  ] = useShowModals(
    Modals.MemberServicesFollowUp,
    Modals.AddComplimentaryPeriod,
    Modals.AddPreSoldMembership,
    Modals.SendMembershipEmail,
    Modals.CustomSignupLink,
    Modals.PauseSubscription,
  );

  const { copyToClipboard } = useCopyToClipboard();
  const { partnerGroupRules } = usePartnerGroupRules(address.partner?.id ?? address.partnerId);
  const { groupRules } = useGroupRules();

  const hasAccessToSubscriptionManagementPlus =
    partnerGroupRules[PartnerGroupRules.AccessToSubscriptionManagementPlus];
  const hasAccessToSubscriptionManagement =
    partnerGroupRules[PartnerGroupRules.AccessToSubscriptionManagement];
  const hasAccessToProvisionProjects =
    partnerGroupRules[PartnerGroupRules.AccessToProvisionProjects];
  const canUpdateSLAs = Boolean(groupRules[UserGroupRules.CanUpdateSLAs]);
  const canGenerateCustomSignupLink = Boolean(
    groupRules[UserGroupRules.AccessToGenerateCustomCheckoutUrl],
  );
  const canPauseSubscription = Boolean(groupRules[UserGroupRules.AccessToPauseSubscription]);

  const topStageProjectForAddress = useMemo(() => {
    const projects = address?.projects;
    if (!projects?.length) return null;
    return Math.max(...projects.map((project) => project?.projectStageId)) || null;
  }, [address?.projects]);

  const showAddPreSoldMembershipOption = useMemo(
    () =>
      canUpdateSLAs &&
      hasAccessToSubscriptionManagement &&
      !address?.preSoldMemberships?.[0]?.isScheduled &&
      (!hasAccessToProvisionProjects ||
        (hasAccessToProvisionProjects &&
          topStageProjectForAddress === ProjectStages.ServiceClient)),
    [
      address?.preSoldMemberships,
      canUpdateSLAs,
      hasAccessToProvisionProjects,
      hasAccessToSubscriptionManagement,
      topStageProjectForAddress,
    ],
  );

  const addressSubscriptionId = useMemo(
    () => getCorrectSubscriptionIdFromAddress(address),
    [address],
  );

  return useMemo(
    () => ({
      accessPaymentPortal: {
        label: 'Access payment portal',
        icon: 'credit_card',
        isAllowed: !!address?.addressId && hasAccessToSubscriptionManagement,
        handler: async () => {
          if (address?.addressId) {
            const url = await generateStripeBillingPortalUrl({ addressId: address.addressId });
            if (url?.url) {
              void copyToClipboard(url.url);
            }
          }
        },
      },
      sendMembershipEmail: {
        label: 'Send membership email',
        icon: 'email',
        isAllowed: !!address?.addressId,
        handler: () => {
          showSendMembershipEmailModal({ address });
        },
      },
      followUp: {
        label: 'Member Services follow-up',
        icon: 'offline_bolt',
        isAllowed: hasAccessToSubscriptionManagementPlus,
        handler: () => {
          showFollowUpModal({ addressId: address?.addressId, conversationId });
        },
      },
      addComplimentaryPeriod: {
        label: 'Add complimentary period',
        icon: 'verified_user',
        isAllowed: hasAccessToSubscriptionManagement && canUpdateSLAs && !isCppDisabled,
        handler: () => {
          showComplimentaryPeriodModal({ address, addCppToSchedule });
        },
      },
      addPreSoldMembership: {
        label: 'Add pre-sold membership',
        icon: 'request_quote',
        isAllowed: showAddPreSoldMembershipOption,
        handler: () => {
          showPreSoldMembershipModal({
            addressId: address?.addressId,
            addPsmToSchedule,
          });
        },
      },
      customSignupLink: {
        label: 'Custom signup link',
        icon: 'link',
        testId: 'membership-info-custom-signup-link-action',
        isAllowed:
          !!address?.addressId && !addressSubscriptionId && canGenerateCustomSignupLink,
        handler: () => {
          showCustomSignupLinkModal({
            address,
            addressSubscriptionId,
          });
        },
      },
      copyCalendlyLink: {
        label: 'Copy Member Services Calendly link',
        icon: 'content_copy',
        isAllowed:
          !!address?.partner?.membershipSalesCalendlyLink &&
          hasAccessToSubscriptionManagementPlus,
        handler: () => {
          if (address?.partner?.membershipSalesCalendlyLink) {
            void copyToClipboard(address.partner.membershipSalesCalendlyLink);
          }
        },
      },
      copySubscriptionId: {
        label: 'Copy subscription ID',
        icon: 'content_copy',
        isAllowed: !!addressSubscriptionId,
        handler: () => {
          void copyToClipboard(addressSubscriptionId || '');
        },
      },
      pauseSubscription: {
        label: 'Pause subscription',
        icon: 'pause_circle',
        isAllowed:
          !!addressSubscriptionId &&
          hasAccessToSubscriptionManagement &&
          canUpdateSLAs &&
          canPauseSubscription &&
          !subscriptionDetails?.paused,
        handler: () => {
          showPauseSubscriptionModal({ address });
        },
      },
      unpauseSubscription: {
        label: 'Unpause subscription',
        icon: 'play_circle',
        isAllowed:
          !!addressSubscriptionId &&
          hasAccessToSubscriptionManagement &&
          canUpdateSLAs &&
          canPauseSubscription &&
          !!subscriptionDetails?.paused,
        handler: () => {
          onUnpauseSubscription?.();
        },
      },
    }),
    [
      address,
      canUpdateSLAs,
      canGenerateCustomSignupLink,
      conversationId,
      copyToClipboard,
      generateStripeBillingPortalUrl,
      canPauseSubscription,
      hasAccessToSubscriptionManagement,
      hasAccessToSubscriptionManagementPlus,
      showAddPreSoldMembershipOption,
      showComplimentaryPeriodModal,
      addCppToSchedule,
      addPsmToSchedule,
      showFollowUpModal,
      showPreSoldMembershipModal,
      showSendMembershipEmailModal,
      showCustomSignupLinkModal,
      showPauseSubscriptionModal,
      addressSubscriptionId,
      subscriptionDetails,
      onUnpauseSubscription,
      isCppDisabled,
    ],
  );
};
